import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import Modal from './Modal';
import { DisplayStand } from '../types';
import { AlertTriangle } from 'lucide-react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStands } from '../context/StandsContext';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import { createNotification, ORG_BROADCAST_USER_ID } from '../lib/notifications';

interface MaintenanceRequestModalProps {
  stand: DisplayStand;
  isOpen: boolean;
  onClose: () => void;
}

const MaintenanceRequestModal: React.FC<MaintenanceRequestModalProps> = ({
  stand,
  isOpen,
  onClose
}) => {
  const { setStands } = useStands();
  const { currentUser } = useAuth();
  const { currentOrganization } = useOrganization();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    description: '',
    issues: '',
    urgency: 'normal' as 'low' | 'normal' | 'high'
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description || !formData.issues) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setLoading(true);
      const standRef = doc(db, 'stands', stand.id);
      
      const uniqueId = (typeof crypto !== 'undefined' && (crypto as any).randomUUID)
        ? (crypto as any).randomUUID()
        : `${Math.random().toString(36).slice(2)}_${Date.now()}`;

      const maintenanceRequest = {
        id: uniqueId,
        type: 'curative' as const,
        date: new Date().toISOString(),
        standId: stand.id,
        standName: stand.name,
        requestedBy: currentUser?.email || currentUser?.uid || 'Utilisateur',
        description: formData.description,
        issues: formData.issues,
        status: 'pending' as const
      };

      await updateDoc(standRef, {
        maintenanceHistory: [
          ...(stand.maintenanceHistory || []),
          {
            ...maintenanceRequest,
            requestedByUid: currentUser?.uid || null,
            urgency: formData.urgency,
            createdAtMs: Date.now()
          }
        ],
        lastUpdated: serverTimestamp()
      });

      // Mettre à jour l'état local
      setStands(prevStands => 
        prevStands.map(s => {
          if (s.id === stand.id) {
            return {
              ...s,
              maintenanceHistory: [
                ...(s.maintenanceHistory || []),
                maintenanceRequest
              ],
              lastUpdated: new Date().toISOString()
            };
          }
          return s;
        })
      );

      // Notification pour l'organisation entière (visible par plusieurs utilisateurs)
      if (currentOrganization?.id) {
        try {
          await createNotification(
            currentOrganization.id,
            ORG_BROADCAST_USER_ID,
            {
              type: 'warning',
              title: 'Nouvelle demande de maintenance',
              message: `Présentoir "${stand.name}" – ${formData.urgency.toUpperCase()} – par ${currentUser?.email || 'Utilisateur'}`,
              category: 'maintenance',
              link: `/stands/${stand.id}`
            }
          );
        } catch (notifyErr) {
          console.warn('Notification non envoyée:', notifyErr);
        }
      }

      toast.success('Demande de maintenance envoyée avec succès');
      onClose();
    } catch (error) {
      console.error('Error submitting maintenance request:', error);
      toast.error('Erreur lors de l\'envoi de la demande');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose}
      title="Demande de maintenance"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg border border-yellow-100 dark:border-yellow-800/50">
          <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-300 mb-2">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">Maintenance curative</span>
          </div>
          <p className="text-sm text-yellow-600 dark:text-yellow-200">
            Utilisez ce formulaire pour signaler un problème nécessitant une intervention.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200  mb-1">
            Description du problème *
          </label>
          <textarea
            className="input bg-white dark:bg-gray-800 dark:text-gray-100"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={3}
            placeholder="Décrivez le problème rencontré"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-200  mb-1">
            Actions déjà tentées *
          </label>
          <textarea
            className="input bg-white dark:bg-gray-800 dark:text-gray-100"
            value={formData.issues}
            onChange={(e) => setFormData({ ...formData, issues: e.target.value })}
            required
            rows={3}
            placeholder="Décrivez les actions que vous avez déjà tentées"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Niveau d'urgence
          </label>
          <select
            className="input"
            value={formData.urgency}
            onChange={(e) => setFormData({ 
              ...formData, 
              urgency: e.target.value as 'low' | 'normal' | 'high'
            })}
          >
            <option value="low">Faible - Peut attendre</option>
            <option value="normal">Normal - À traiter dès que possible</option>
            <option value="high">Urgent - Nécessite une intervention rapide</option>
          </select>
        </div>

        <div className="flex justify-end space-x-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            Annuler
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Envoi en cours...
              </div>
            ) : (
              'Envoyer la demande'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default MaintenanceRequestModal;
